import type { ReactNode } from 'react'

/**
 * Column definitions for `DataTable`, in their own module so the table
 * component stays under the size cap and so a screen can describe its
 * columns without importing the renderer.
 *
 * A column is data, not JSX: that is what lets the table own the empty,
 * loading and error states, the sticky header and the row semantics
 * instead of every caller hand-rolling a `<table>` again — which is how
 * this app ended up with a different table on every page.
 */

export type ColumnAlign = 'left' | 'right' | 'center'

export interface Column<T> {
  /** Stable identity for the React key and for `getRowKey`-style lookups.
   * Not the header text: headers get renamed, keys must not. */
  key: string
  /** Lower case, always — `status`, not `Status`.
   *
   * It is a convention rather than a transform so a header can still be a
   * node (an environment name, a `<span title>`). The parallel design-system
   * migration left the sessions and vault groups on lower case and the
   * Composio, Alerts and Dashboard groups on Title Case, which read as two
   * different products; lower case won because it is what 30 of the 45
   * columns already used and because a 12px tracked-out header is quieter
   * unshifted — the header is a column's name, not a label shouting at the
   * operator. */
  header: ReactNode
  render: (row: T) => ReactNode
  /** Numbers, durations and counts go `right` so their digits line up
   * column-wise — the only way a wall of them is scannable. Default
   * `left`. */
  align?: ColumnAlign
  /** A CSS length for the `<col>`, e.g. `'8rem'`. Optional on purpose:
   * fixing every width is how a table stops fitting its content. Fix the
   * narrow, predictable ones (status, timestamp) and let the rest flow. */
  width?: string
  /** Machine values — ids, node paths, hashes. Same reasoning as `Code`:
   * these get compared character by character. */
  isMono?: boolean
}

const ALIGN_CLASSES: Record<ColumnAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
}

/** The alignment utility for a column. Written as a lookup rather than an
 * interpolated `text-${align}` because Tailwind v4 scans source text and
 * never emits a class that is assembled at runtime. */
export function columnAlignClass(align: ColumnAlign = 'left'): string {
  return ALIGN_CLASSES[align]
}

/** The class pair for one body cell: 12px, tight padding, and monospace
 * where the column asked for it. `--spacing-row` on the row plus `py-1`
 * here is what produces the 32px row the density depends on. */
export function cellClassName<T>(column: Column<T>): string {
  return `truncate px-2 py-1 text-xs ${columnAlignClass(column.align)} ${
    column.isMono === true ? 'font-mono' : ''
  }`
}
